/**
 * Por comprar: what is running out, and how much of it to buy.
 *
 * The list in the notebook behind the counter, worked out. For each product, how fast it has
 * been going over the last four weeks — sold, less what customers brought back, plus what went
 * into bags — against what is on the shelves, in the storeroom and on the truck. What will not
 * last until the next buy goes on the list, grouped by whoever sold it to the shop last, since
 * that is whom it gets asked of when their salesperson comes round — or by shelf, for a trip to
 * the market. What the shop bags itself goes in a group of its own: it gets repacked, not bought.
 *
 * A suggestion, not an order. The distributors' salespeople take the order at the counter, on
 * their round; it is not sent to them (the owner, 2026-10-16, of the «Pedir por WhatsApp» that
 * was here: «that's not how these distributors mostly work»). So nothing here sends anything:
 * what is short, how much, and what it cost the last times, to have at hand when they come.
 *
 * A minimum set on the product is a floor. At or below it the product is on the list whatever
 * the pace says, and the suggestion leaves the minimum still there at the end of the period.
 *
 * A product's pace is spread over the days it has been in the shop. That is the shop's own days
 * of the four weeks, from its first sale, unless the product came in after that: then from the day
 * it came, a week at least. It came on its first purchase, the first count that found it, or the
 * first time it was bagged here, or on its first sale if that was earlier still. Until 2026-10-18
 * every product was spread over the shop's days, so one added a week ago showed a quarter of its
 * pace and was bought too little, too late. That day 103 of the 230 products the live shop had
 * sold had come in after it opened.
 *
 * What sold in an offer («Ofertas», 2026-11-03) is left out of the pace: it went because it was
 * cheaper, and buying for it would fill the shelf the offer was there to empty.
 *
 * Pure, so it can be tested; `Reponer.tsx` hands it the tables.
 */

import {
  diasEntre,
  mulDiv,
  saleToBase,
  subQty,
  sumarDias,
  ZERO_QTY,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import { esDeudaAnterior, type Compra, type Conteo, type Existencia, type Producto, type Reempaque, type Venta } from '@abastece/core';
import { normalizar } from './texto.js';

/** How far back the pace is measured. Four weeks: long enough to cover a payday, short enough to follow a season. */
export const VENTANA_DIAS = 28;

/** How long what gets bought should last, in weeks. */
export const COBERTURAS = [1, 2, 4] as const;
export type Cobertura = (typeof COBERTURAS)[number];

/** At this many days of stock or fewer it cannot wait for the usual buying day. */
export const DIAS_URGENTE = 3;

/**
 * A shop with a few days of sales has a few days of sales, not four weeks' worth — but a week is
 * the least the pace is spread over, so one busy first morning does not order a truckload.
 */
const DIAS_MINIMOS = 7;

export type MotivoReponer = 'agotado' | 'minimo' | 'ritmo';

export interface Sugerencia {
  producto: Producto;
  /** Everywhere together, in base units. Can be negative when more was sold than recorded. */
  existencia: Milli;
  /** Base units a day, in milli, over the window, or since it came in (`medidoDesde`). */
  porDia: number;
  /**
   * The day it came into the shop, when it came after the shop's first sale of the four weeks and
   * its pace is spread over fewer days for it. Null when the shop's days are its days.
   */
  medidoDesde: string | null;
  /** Days the stock lasts at that pace. Null when it is not moving. */
  dias: number | null;
  minimo: Milli | null;
  /** What to buy, in whole base units. Zero when there is nothing to go by: the owner says. */
  cantidad: Milli;
  motivo: MotivoReponer;
  urgente: boolean;
  /** Who sold it to the shop last. Null when it was never bought here. */
  proveedorId: string | null;
  /** What `cantidad` cost at the last purchase's landed price. Null when never bought. */
  estimado: Centavos | null;
  /** The product this one is bagged from, when the shop makes it rather than buys it. */
  reempacaDe: string | null;
  /** The last day it sold in the four weeks; null when it did not. Out of stock, about since when. */
  ultimaVenta: string | null;
  /** The last time it was bought: the day, how much, and what that cost landed. Null when never. */
  ultimaCompra: { fecha: string; cantidad: Milli; costo: Centavos } | null;
}

export interface DatosReponer {
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  /** At least the window's sales. Voided ones and older ones are left out here. */
  ventas: readonly Venta[];
  compras: readonly Compra[];
  reempaques: readonly Reempaque[];
  /** The stock counts: one that found a product on the shelf says it was in the shop by then. */
  conteos: readonly Conteo[];
  hoy: string;
  cobertura: Cobertura;
}

const unidadEntera = (m: number): Milli => (Math.ceil(m / 1000) * 1000) as Milli;

export function sugerirCompras(d: DatosReponer): Sugerencia[] {
  const desde = sumarDias(d.hoy, -(VENTANA_DIAS - 1));
  const ventas = d.ventas.filter((v) => !v.anulada && v.fecha >= desde && v.fecha <= d.hoy);
  const primera = ventas.reduce<string | null>((a, v) => (a === null || v.fecha < a ? v.fecha : a), null);
  const dias = primera === null ? VENTANA_DIAS : Math.max(DIAS_MINIMOS, diasEntre(primera, d.hoy) + 1);

  // What left, in base units: sold less brought back, and consumed into bags.
  const salio = new Map<string, number>();
  const sumar = (productoId: string, base: number) => salio.set(productoId, (salio.get(productoId) ?? 0) + base);
  const vendido = new Map<string, string>();
  /** Each product's first sale of the four weeks, whatever came back of it. */
  const primeraVenta = new Map<string, string>();
  for (const v of ventas) {
    for (const l of v.lineas) {
      if (!l.productoId) continue;
      if (v.fecha < (primeraVenta.get(l.productoId) ?? '9999')) primeraVenta.set(l.productoId, v.fecha);
      const neta = subQty(l.cantidad, l.devuelta ?? ZERO_QTY);
      if (neta > 0) {
        // What an offer sold is not the pace to buy for: it went because it was cheaper (2026-11-03).
        if (!l.ofertaId) sumar(l.productoId, saleToBase(neta, l.factor));
        if (v.fecha > (vendido.get(l.productoId) ?? '')) vendido.set(l.productoId, v.fecha);
      }
    }
  }
  for (const r of d.reempaques) {
    if (r.fecha >= desde && r.fecha <= d.hoy) sumar(r.origenProductoId, r.origenCantidad);
  }

  // When each product came into the shop: the first time it was bought, found on the shelf by a
  // count, or bagged here; or sold, if that was earlier still. One with none of the first three
  // has nothing to say when it came, and keeps the shop's days.
  const llego = new Map<string, string>();
  const llegoEl = (productoId: string, fecha: string) => {
    if (fecha < (llego.get(productoId) ?? '9999')) llego.set(productoId, fecha);
  };
  for (const c of d.compras) {
    if (c.anulada) continue;
    for (const l of c.lineas) if (l.cantidadBase > 0) llegoEl(l.productoId, c.fecha);
  }
  for (const k of d.conteos) for (const l of k.lineas) if (l.cantidadContada > 0) llegoEl(l.productoId, k.fecha);
  for (const r of d.reempaques) for (const x of r.destinos) llegoEl(x.productoId, r.fecha);
  /** From when a product's pace is measured, when that is later than the shop's first sale. */
  const desdeQueLlego = (productoId: string): string | null => {
    const llegada = llego.get(productoId);
    if (llegada === undefined || primera === null) return null;
    const vendio = primeraVenta.get(productoId);
    const desdeEl = vendio !== undefined && vendio < llegada ? vendio : llegada;
    return desdeEl > primera ? desdeEl : null;
  };

  const hay = new Map<string, number>();
  for (const e of d.existencias) hay.set(e.productoId, (hay.get(e.productoId) ?? 0) + e.cantidad);

  // The last time each product came in: bought, and from whom, or bagged, and from what.
  const ultimaCompra = new Map<string, { fecha: string; proveedorId: string; costo: Centavos; base: Milli }>();
  for (const c of [...d.compras].filter((c) => !c.anulada).sort((a, b) => a.fecha.localeCompare(b.fecha))) {
    for (const l of c.lineas) {
      if (l.cantidadBase > 0) {
        ultimaCompra.set(l.productoId, {
          fecha: c.fecha,
          proveedorId: c.proveedorId,
          costo: l.costoCordobas,
          base: l.cantidadBase,
        });
      }
    }
  }
  const ultimoReempaque = new Map<string, { fecha: string; origen: string }>();
  for (const r of [...d.reempaques].sort((a, b) => a.fecha.localeCompare(b.fecha))) {
    for (const x of r.destinos) ultimoReempaque.set(x.productoId, { fecha: r.fecha, origen: r.origenProductoId });
  }

  const semanas = d.cobertura * 7;
  const lista: Sugerencia[] = [];
  for (const producto of d.productos) {
    if (!producto.activo) continue;
    const existencia = (hay.get(producto.id) ?? 0) as Milli;
    const llegada = desdeQueLlego(producto.id);
    // The days since it came, a week at least as the shop's are: only ever fewer than the shop's.
    const suyos = llegada === null ? dias : Math.max(DIAS_MINIMOS, diasEntre(llegada, d.hoy) + 1);
    const porDia = (salio.get(producto.id) ?? 0) / Math.min(dias, suyos);
    const minimo = producto.stockMinimo ?? null;
    const compra = ultimaCompra.get(producto.id);
    const reempaque = ultimoReempaque.get(producto.id);
    const necesita = porDia * semanas;

    let motivo: MotivoReponer | null = null;
    // Out of something nobody has ever bought, bagged or sold is not news.
    if (existencia <= 0 && (porDia > 0 || minimo !== null || compra || reempaque)) motivo = 'agotado';
    else if (minimo !== null && existencia <= minimo) motivo = 'minimo';
    else if (porDia > 0 && necesita > existencia) motivo = 'ritmo';
    if (motivo === null) continue;

    // Enough for the period, with the minimum still there at the end of it. A negative count
    // is a recording slip, not goods owed to the shelf.
    let cantidad = Math.max(0, necesita + (minimo ?? 0) - Math.max(0, existencia));
    // Nothing to go by but the last order: that is the usual size of one.
    if (cantidad === 0 && compra) cantidad = compra.base;
    const entera = unidadEntera(cantidad);

    const seReempaca = reempaque !== undefined && (compra === undefined || reempaque.fecha >= compra.fecha);
    const diasQueAlcanza = porDia > 0 ? Math.max(0, existencia) / porDia : null;
    lista.push({
      producto,
      existencia,
      porDia,
      medidoDesde: suyos < dias ? llegada : null,
      dias: diasQueAlcanza,
      minimo,
      cantidad: entera,
      motivo,
      urgente: existencia <= 0 || (diasQueAlcanza !== null && diasQueAlcanza <= DIAS_URGENTE),
      proveedorId: seReempaca ? null : (compra?.proveedorId ?? null),
      estimado: compra && !seReempaca ? mulDiv(compra.costo, entera, compra.base) : null,
      reempacaDe: seReempaca ? reempaque.origen : null,
      ultimaVenta: vendido.get(producto.id) ?? null,
      ultimaCompra: compra ? { fecha: compra.fecha, cantidad: compra.base, costo: compra.costo } : null,
    });
  }

  return lista.sort(
    (a, b) =>
      Number(b.urgente) - Number(a.urgente) ||
      (a.dias ?? Infinity) - (b.dias ?? Infinity) ||
      a.producto.nombre.localeCompare(b.producto.nombre, 'es'),
  );
}

/** Whether a product belongs in the home screen's warning: out, at its minimum, or days from out. */
export const seEstaAcabando = (s: Sugerencia): boolean => s.urgente || s.motivo === 'minimo';

export interface GrupoReponer {
  /** A supplier's id; `reempaque` for what is bagged here; `ninguno` for never bought; `categoria:…` by shelf. */
  clave: string;
  proveedorId: string | null;
  /** By shelf: the category, as most of its products spell it. Null by supplier. */
  categoria: string | null;
  sugerencias: Sugerencia[];
}

const cuantasUrgentes = (g: GrupoReponer) => g.sugerencias.filter((s) => s.urgente).length;

/**
 * One list per supplier, for when their salesperson comes. Suppliers with something urgent first,
 * then the most lines; then what gets bagged here; then what was never bought, which needs a
 * supplier before it can be asked for.
 */
export function agruparPorProveedor(sugerencias: readonly Sugerencia[]): GrupoReponer[] {
  const grupos = new Map<string, GrupoReponer>();
  for (const s of sugerencias) {
    const clave = s.reempacaDe ? 'reempaque' : (s.proveedorId ?? 'ninguno');
    const g = grupos.get(clave) ?? {
      clave,
      proveedorId: s.reempacaDe ? null : s.proveedorId,
      categoria: null,
      sugerencias: [],
    };
    g.sugerencias.push(s);
    grupos.set(clave, g);
  }
  const lugar = (g: GrupoReponer) => (g.clave === 'reempaque' ? 1 : g.clave === 'ninguno' ? 2 : 0);
  return [...grupos.values()].sort(
    (a, b) => lugar(a) - lugar(b) || cuantasUrgentes(b) - cuantasUrgentes(a) || b.sugerencias.length - a.sugerencias.length,
  );
}

/** A capital to start, and the accents: «Granos básicos» over «granos basicos». */
const esmero = (nombre: string): number =>
  (/^\p{Lu}/u.test(nombre) ? 2 : 0) + (nombre.normalize('NFD') !== nombre ? 1 : 0);

/**
 * One list per shelf, for a trip to the market: what is short of each category together, whoever
 * sold it. A category is typed by hand, so «Limpieza» and «limpieza » are the same shelf, named as
 * most of its products spell it. The shelves with the most that cannot wait first, then the
 * longest lists, then by name.
 */
export function agruparPorCategoria(sugerencias: readonly Sugerencia[]): GrupoReponer[] {
  const grupos = new Map<string, { sugerencias: Sugerencia[]; nombres: Map<string, number> }>();
  for (const s of sugerencias) {
    const nombre = s.producto.categoria.trim().replace(/\s+/g, ' ') || 'Otros';
    const clave = normalizar(nombre);
    const g = grupos.get(clave) ?? { sugerencias: [], nombres: new Map<string, number>() };
    g.sugerencias.push(s);
    g.nombres.set(nombre, (g.nombres.get(nombre) ?? 0) + 1);
    grupos.set(clave, g);
  }
  return [...grupos.entries()]
    .map(
      ([clave, g]): GrupoReponer => ({
        clave: `categoria:${clave}`,
        proveedorId: null,
        // The commonest spelling; between two as common, the one written with more care.
        categoria: [...g.nombres].sort((a, b) => b[1] - a[1] || esmero(b[0]) - esmero(a[0]) || a[0].localeCompare(b[0], 'es'))[0]![0],
        sugerencias: g.sugerencias,
      }),
    )
    .sort(
      (a, b) =>
        cuantasUrgentes(b) - cuantasUrgentes(a) ||
        b.sugerencias.length - a.sugerencias.length ||
        a.categoria!.localeCompare(b.categoria!, 'es'),
    );
}

export interface ResumenReponer {
  agotados: number;
  /** Not out yet, and out within `DIAS_URGENTE` days at its pace. */
  pronto: number;
  /** What the suggestions come to at each one's last landed cost. */
  estimado: Centavos;
  /** Never bought here, so left out of `estimado`: no cost to go by. What is bagged here is not bought. */
  sinCosto: number;
}

/** The list at a glance: how much of it is out, how much nearly, and about what it all costs. */
export function resumenReponer(sugerencias: readonly Sugerencia[]): ResumenReponer {
  return {
    agotados: sugerencias.filter((s) => s.existencia <= 0).length,
    pronto: sugerencias.filter((s) => s.urgente && s.existencia > 0).length,
    estimado: sugerencias.reduce((a, s) => a + (s.estimado ?? 0), 0) as Centavos,
    sinCosto: sugerencias.filter((s) => s.estimado === null && !s.reempacaDe).length,
  };
}

export interface CompraDelProducto {
  compraId: string;
  fecha: string;
  proveedorId: string;
  /** What came in, in base units. */
  cantidad: Milli;
  /** What the line cost landed, freight and IVA in, in córdobas. */
  costo: Centavos;
}

/** The last times a product was bought, the latest first: from whom, how much, and what it cost landed. */
export function comprasDelProducto(productoId: string, compras: readonly Compra[], cuantas = 3): CompraDelProducto[] {
  const filas: CompraDelProducto[] = [];
  for (const c of compras) {
    if (c.anulada) continue;
    for (const l of c.lineas) {
      if (l.productoId === productoId && l.cantidadBase > 0) {
        filas.push({ compraId: c.id, fecha: c.fecha, proveedorId: c.proveedorId, cantidad: l.cantidadBase, costo: l.costoCordobas });
      }
    }
  }
  // Of two the same day, the one recorded later is the last, as `sugerirCompras` takes it.
  return filas
    .reverse()
    .sort((a, b) => b.fecha.localeCompare(a.fecha))
    .slice(0, cuantas);
}

/** The day each supplier last brought goods: voided purchases, and debts from before the app, left out. */
export function ultimaCompraPorProveedor(compras: readonly Compra[]): Map<string, string> {
  const ultima = new Map<string, string>();
  for (const c of compras) {
    if (c.anulada || esDeudaAnterior(c)) continue;
    if (c.fecha > (ultima.get(c.proveedorId) ?? '')) ultima.set(c.proveedorId, c.fecha);
  }
  return ultima;
}
